import {isArray} from 'lodash';

import {hasProperty} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {AppRoutesPrefixesEnum} from '../../../components/appRoutesPrefixes';
import {standaloneComposerPath} from '../../../helpers/composerHelper';
import {parseMailToFromLocation} from '../../../helpers/standaloneComposerHelpers';
import {AppState} from '../../appState';
import {getHasVisitorExperience, getHasVisitorExperienceV3} from '../../featureFlags/featureFlagsSelectors';
import {AnalyticsPanelRoute, buildAnalyticsPanelRoutes} from '../analytics/analyticsPanelRoutes';
import {
  buildContactsManagerPanelRoutes,
  ContactsManagerPanelRoute,
} from '../contacts/contactsManager/contactsManagerPanelRoutes';
import {buildInboxesPanelRoutes, InboxesPanelRoute} from '../inboxes/inboxesPanelRoutes';
import {PanelRoute} from '../panelRoutes';
import {AppContentTypesEnum, AppParameters} from './parameters/appParameters';

/*
 * Types.
 */

type AppPanelRoute = PanelRoute<AppState, AppParameters | undefined>;

/*
 * Routes.
 */

export function buildAppPanelRoutes(): ReadonlyArray<AppPanelRoute> {
  return [
    ...buildInboxesPanelRoutes().map(buildInboxesPanelRoute),
    ...buildContactsManagerPanelRoutes().map(buildContactsManagerPanelRoute),
    ...buildAnalyticsPanelRoutes().map(buildAnalyticsPanelRoute),
    {
      path: `/(${AppRoutesPrefixesEnum.CALENDARS}|${AppRoutesPrefixesEnum.CALENDAR})`,
      getParameters: () => ({contentType: AppContentTypesEnum.CALENDARS, parameters: undefined}),
    },
    {
      path: `/${AppRoutesPrefixesEnum.CONTACTS_MANAGER}/:rest*`,
      getParameters: (_state, {params}) => ({
        contentType: AppContentTypesEnum.CONTACTS,
        location: {
          pathname: `/${AppRoutesPrefixesEnum.CONTACTS_MANAGER}${formatPathRest(params)}`,
        },
      }),
    },
    {
      path: `/${AppRoutesPrefixesEnum.ANALYTICS}/:rest*`,
      getParameters: (_state, {params, search}) => ({
        contentType: AppContentTypesEnum.ANALYTICS,
        location: {
          pathname: `/${AppRoutesPrefixesEnum.ANALYTICS}${formatPathRest(params)}`,
          search,
        },
      }),
    },
    buildLegacyRoute(`/${AppRoutesPrefixesEnum.SEQUENCES}`, AppContentTypesEnum.SEQUENCES),
    buildLegacyRoute(`/${AppRoutesPrefixesEnum.AUDIT_TRAIL}`, AppContentTypesEnum.AUDIT_TRAIL),
    buildLegacyRoute(`/${AppRoutesPrefixesEnum.SETTINGS}`, AppContentTypesEnum.SETTINGS),
    {
      path: `/(${AppRoutesPrefixesEnum.VISITORS}|${AppRoutesPrefixesEnum.V2VISITORS})`,
      getParameters: (state) => {
        if (getHasVisitorExperienceV3(state)) {
          return {contentType: AppContentTypesEnum.VISITOR_EXPERIENCE_V3};
        }

        if (getHasVisitorExperience(state)) {
          return {contentType: AppContentTypesEnum.VISITOR_EXPERIENCE};
        }

        return {contentType: AppContentTypesEnum.VISITOR_VIEWER};
      },
    },
    {
      path: standaloneComposerPath,
      getParameters: (_state, props) => ({
        contentType: AppContentTypesEnum.STANDALONE_COMPOSER,
        mailtoOptions: parseMailToFromLocation(props),
      }),
    },
    {
      path: '/',
      getParameters: () => undefined,
    },
  ];
}

function buildInboxesPanelRoute(route: InboxesPanelRoute): AppPanelRoute {
  return {
    path: `/${AppRoutesPrefixesEnum.INBOXES}${route.path}`,
    getParameters: (state, props) => ({
      contentType: AppContentTypesEnum.INBOXES,
      parameters: route.getParameters(state, props),
    }),
  };
}

function buildContactsManagerPanelRoute(route: ContactsManagerPanelRoute): AppPanelRoute {
  return {
    path: `/${AppRoutesPrefixesEnum.CONTACTS_MANAGER}${route.path}`,
    getParameters: (state, props) => ({
      contentType: AppContentTypesEnum.CONTACTS,
      parameters: route.getParameters(state, props),
    }),
  };
}

function buildLegacyRoute<T extends AppContentTypesEnum>(
  basePath: string,
  contentType: T,
): PanelRoute<unknown, {contentType: T; path: string}> {
  return {
    path: `${basePath}/:rest*`,
    getParameters: (_state, {params, search}) => ({
      contentType,
      path: `${basePath}${formatPathRest(params)}${search}`,
    }),
  };
}

function buildAnalyticsPanelRoute(route: AnalyticsPanelRoute): AppPanelRoute {
  return {
    path: `/${AppRoutesPrefixesEnum.ANALYTICS}${route.path}`,
    getParameters: (state, props) => ({
      contentType: AppContentTypesEnum.ANALYTICS,
      parameters: route.getParameters(state, props),
    }),
  };
}

function formatPathRest(params: unknown): string {
  if (!hasProperty(params, 'rest') || !isArray(params.rest)) {
    return '';
  }

  return `/${params.rest.join('/')}`;
}
